import React, { useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight, Check, Wand2, Rocket, Sparkles, Image, Bot, Phone, Globe,
  Megaphone, TrendingUp, Target, Workflow, Zap, Search, Database, LogIn,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  PRODUCT_BRAND,
  getProductBySlug,
  getRelatedProducts,
  productShowcases,
} from '../data/productShowcases';
import {
  MarketingNav,
  MarketingFooter,
  FloatingOrbs,
  fadeUp,
  AnimatedSection,
} from './IndustryCaseStudy';
import '../styles/landing-clean.css';

const ICONS = {
  Wand2, Rocket, Sparkles, Image, Bot, Phone, Globe,
  Megaphone, TrendingUp, Target, Workflow, Zap, Search, Database,
};

const authWithNext = (loginUrl, appPath) => {
  const base = loginUrl || '/auth';
  if (!appPath) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}next=${encodeURIComponent(appPath)}`;
};

const ProductShowcase = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = getProductBySlug(slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '16%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) {
    return (
      <div className="landing-clean min-h-screen" data-testid="product-not-found">
        <MarketingNav />
        <div className="max-w-xl mx-auto px-6 pt-36 pb-20 text-center">
          <h1 className="font-heading text-4xl font-extrabold text-[#16130d] mb-4">Product not found</h1>
          <p className="text-[#5c5245] mb-8">That product page doesn’t exist. Browse the full catalog instead.</p>
          <Button onClick={() => navigate('/products')} className="!bg-[#16130d] !text-white rounded-full font-bold">
            All products
          </Button>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  const Icon = ICONS[product.icon] || Sparkles;
  const loginHref = authWithNext(product.loginUrl, product.appPath);
  const related = getRelatedProducts(product.slug, 3);

  return (
    <div className="landing-clean min-h-screen overflow-x-hidden" data-testid={`product-page-${product.slug}`}>
      <MarketingNav />

      <section
        ref={heroRef}
        className="relative pt-28 pb-16 px-6 md:px-12 overflow-hidden bg-[#fbbf0f]"
      >
        <FloatingOrbs />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(rgba(28,25,23,0.04) 1px, transparent 1px)',
            backgroundSize: '14px 14px',
          }}
        />
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0} className="flex items-center gap-2 mb-4">
                <Link to="/products" className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#16130d]/70 hover:text-[#16130d]">
                  Products
                </Link>
                <span className="text-[#16130d]/40">/</span>
                <span className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#16130d]">{product.tag}</span>
              </motion.div>
              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={1}
                className="font-heading text-sm font-extrabold text-[#16130d] mb-3"
              >
                {product.eyebrow}
              </motion.p>
              <motion.h1
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={2}
                className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#16130d] mb-4"
                data-testid="product-title"
              >
                {product.title}
              </motion.h1>
              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={3}
                className="text-lg md:text-xl text-[#3a3327] font-medium leading-relaxed mb-8 max-w-xl"
              >
                {product.hero}
              </motion.p>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={4}
                className="flex flex-wrap gap-3"
              >
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    onClick={() => navigate(loginHref)}
                    className="!bg-[#16130d] !text-white hover:!bg-black rounded-full px-7 py-3 font-bold shadow-[0_5px_0_0_rgba(0,0,0,0.35)]"
                    data-testid="product-login-cta"
                  >
                    <LogIn className="mr-2 h-4 w-4" /> Login to open
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    onClick={() => navigate(loginHref)}
                    className="!bg-white !text-[#16130d] border-2 border-[#16130d] rounded-full px-7 py-3 font-bold shadow-[0_5px_0_0_#16130d]"
                    data-testid="product-signup-cta"
                  >
                    Get started free <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
              </motion.div>
              <p className="mt-4 text-xs font-semibold text-[#5c5245]">
                After login you’ll land in <span className="text-[#16130d] font-extrabold">{product.appPath}</span>
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 28, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-4 rounded-[2rem] bg-[#16130d]/10 blur-2xl" />
              <div className="relative rounded-[2rem] border-2 border-[#16130d] bg-white shadow-[0_12px_0_0_#16130d] overflow-hidden">
                <div className="h-12 border-b-2 border-[#16130d] bg-[#fdf3ce] flex items-center px-4 gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#16130d]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#16130d]/40" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#16130d]/20" />
                  <span className="ml-3 text-xs font-bold text-[#16130d]/70 tracking-wide">
                    {product.slug}.snagcart.app
                  </span>
                </div>
                <div className="p-7 md:p-8 bg-[#fffefb]">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fbbf0f] border-2 border-[#16130d] text-[#16130d] shadow-[0_3px_0_0_#16130d]">
                      <Icon className="h-6 w-6" />
                    </span>
                    <div>
                      <p className="text-xs font-extrabold uppercase tracking-wide text-[#8a8072]">{product.subtitle}</p>
                      <p className="font-heading text-xl font-extrabold text-[#16130d]">{product.title}</p>
                    </div>
                  </div>
                  <p className="text-sm text-[#5c5245] leading-relaxed mb-6">{product.desc}</p>
                  <div className="space-y-2.5">
                    {product.features.slice(0, 4).map((f, i) => (
                      <motion.div
                        key={f}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.35 + i * 0.08 }}
                        className="flex items-start gap-2.5 text-sm font-semibold text-[#16130d]"
                      >
                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#fbbf0f] border border-[#16130d]">
                          <Check className="h-3 w-3" />
                        </span>
                        {f}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <section className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl md:text-4xl font-extrabold text-[#16130d] mb-3">
              What’s included
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-[#5c5245] font-medium mb-10 max-w-2xl">
              {product.desc}
            </motion.p>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-4">
            {product.features.map((f, i) => (
              <AnimatedSection key={f}>
                <motion.div
                  variants={fadeUp}
                  custom={i % 4}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl border-2 border-[#16130d]/10 bg-[#fffefb] px-5 py-4 flex items-start gap-3 shadow-[0_6px_0_0_rgba(22,19,13,0.05)]"
                >
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#fbbf0f] border-2 border-[#16130d] text-[#16130d]">
                    <Check className="h-4 w-4" />
                  </span>
                  <p className="text-sm font-semibold text-[#16130d] leading-relaxed pt-1">{f}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 md:px-12 bg-[#fffdf6]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl md:text-4xl font-extrabold text-[#16130d] mb-10 text-center">
              Why teams use {product.title}
            </motion.h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-5">
            {(product.benefits || []).map((b, i) => (
              <AnimatedSection key={b.title}>
                <motion.div
                  variants={fadeUp}
                  custom={i}
                  whileHover={{ y: -6 }}
                  className="h-full rounded-[1.5rem] border-2 border-[#16130d] bg-[#fbbf0f] p-6 shadow-[0_8px_0_0_#16130d]"
                >
                  <p className="font-heading text-xl font-extrabold text-[#16130d] mb-2">{b.title}</p>
                  <p className="text-sm font-medium text-[#3a3327] leading-relaxed">{b.body}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-[#16130d]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-3">
            Open {product.title} in {PRODUCT_BRAND}
          </h2>
          <p className="text-[#c4b8a5] font-medium mb-8">
            Login to jump into <span className="text-[#fbbf0f]">{product.appPath}</span>
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Button
                onClick={() => navigate(loginHref)}
                className="!bg-[#fbbf0f] !text-[#16130d] hover:!bg-[#ffd84a] rounded-full px-8 py-3 font-bold border-2 border-[#fbbf0f]"
              >
                Login <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Button
                onClick={() => navigate('/products')}
                className="!bg-transparent !text-white border-2 border-white/30 hover:!bg-white/10 rounded-full px-8 py-3 font-bold"
              >
                Browse all products
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl font-extrabold text-[#16130d] mb-6">More products</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((p) => {
              const RIcon = ICONS[p.icon] || Sparkles;
              return (
                <Link
                  key={p.slug}
                  to={`/products/${p.slug}`}
                  className="rounded-2xl border-2 border-[#16130d]/10 bg-[#fffefb] p-5 hover:border-[#16130d]/35 transition-colors"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#fbbf0f] border border-[#16130d] text-[#16130d] mb-3">
                    <RIcon className="h-4 w-4" />
                  </span>
                  <p className="font-heading font-extrabold text-[#16130d]">{p.title}</p>
                  <p className="text-xs font-semibold text-[#8a8072] mt-1">{p.subtitle}</p>
                </Link>
              );
            })}
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {productShowcases.slice(0, 8).map((p) => (
              <Link
                key={p.slug}
                to={`/products/${p.slug}`}
                className={`text-xs font-bold rounded-full px-3 py-1.5 border ${
                  p.slug === product.slug
                    ? 'bg-[#fbbf0f] border-[#16130d] text-[#16130d]'
                    : 'bg-white border-[#16130d]/15 text-[#5c5245] hover:border-[#16130d]/40'
                }`}
              >
                {p.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ProductShowcase;
